import { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Check, X, Clock, CalendarDays, LogIn, LogOut as LogOutIcon,
  Timer, MapPin, MessageSquare
} from "lucide-react";

const COLORS = { Present: "#059669", Absent: "#DC2626", Late: "#D97706", "Half Day": "#0F2E4A" };

export default function Attendance() {
  const { user } = useAuth();
  if (user.role !== "student") return <AdminAttendance />;
  return <StudentAttendance />;
}

function deriveStatus(ci, co) {
  if (!ci) return "Absent";
  if (ci > "10:00") return "Late";
  if (!co) return "Half Day";
  return "Present";
}

/* ============================================================
 *  STUDENT SELF-SERVICE ATTENDANCE
 * ============================================================ */

function StudentAttendance() {
  const [today, setToday] = useState(null);   // { date, server_time, record }
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionKind, setActionKind] = useState(null);  // "in" | "out"
  const [remarks, setRemarks] = useState("");
  const [now, setNow] = useState(new Date());

  // live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const [t, r, s] = await Promise.all([
        api.get("/attendance/today"),
        api.get("/attendance"),
        api.get("/attendance/summary"),
      ]);
      setToday(t.data);
      setRecords(r.data);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to load attendance");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    setBusy(true);
    try {
      const url = actionKind === "in" ? "/attendance/check-in" : "/attendance/check-out";
      const { data } = await api.post(url, { remarks: remarks || null });
      toast.success(
        actionKind === "in"
          ? `Checked in at ${data.check_in}`
          : `Checked out at ${data.check_out} · ${data.total_hours}h`
      );
      setActionKind(null);
      setRemarks("");
      await load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
    setBusy(false);
  };

  const rec = today?.record;
  const checkedIn = !!rec?.check_in;
  const checkedOut = !!rec?.check_out;

  const istTime = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
  const istDate = now.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "Asia/Kolkata" });

  return (
    <div className="space-y-6" data-testid="attendance-student">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Attendance</div>
        <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Self Check-in</h1>
      </div>

      {/* Live clock + action card */}
      <div className="relative overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#0F2E4A]/5" />
        <div className="absolute right-16 bottom-0 w-40 h-40 rounded-full bg-[#059669]/5" />
        <div className="relative p-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left: live time */}
          <div>
            <div className="flex items-center gap-2 text-xs text-[#64748B] uppercase tracking-[0.2em] font-semibold">
              <MapPin className="w-3.5 h-3.5" /> IST · Asia/Kolkata
            </div>
            <div className="font-heading text-[5rem] leading-none font-bold text-[#0F172A] mt-3 tabular-nums" data-testid="live-clock">
              {istTime}
            </div>
            <div className="text-sm text-[#475569] mt-2">{istDate}</div>
            <div className="mt-6 flex items-center gap-6 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Check-in</div>
                <div className={`font-mono-plex text-lg font-semibold ${checkedIn ? "text-[#059669]" : "text-[#94A3B8]"}`} data-testid="today-checkin">
                  {rec?.check_in || "—"}
                </div>
              </div>
              <div className="w-px h-10 bg-[#E2E8F0]" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Check-out</div>
                <div className={`font-mono-plex text-lg font-semibold ${checkedOut ? "text-[#DC2626]" : "text-[#94A3B8]"}`} data-testid="today-checkout">
                  {rec?.check_out || "—"}
                </div>
              </div>
              <div className="w-px h-10 bg-[#E2E8F0]" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Hours</div>
                <div className="font-mono-plex text-lg font-semibold text-[#0F172A]" data-testid="today-hours">
                  {rec?.total_hours ? `${rec.total_hours}h` : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex flex-col gap-4">
            <Button
              size="lg"
              onClick={() => { setActionKind("in"); setRemarks(""); }}
              disabled={checkedIn || busy}
              data-testid="check-in-button"
              className="h-20 text-lg bg-[#059669] hover:bg-[#047857] text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <LogIn className="w-6 h-6 mr-3" strokeWidth={2} />
              {checkedIn ? `Checked in at ${rec.check_in}` : "Check In"}
            </Button>
            <Button
              size="lg"
              onClick={() => { setActionKind("out"); setRemarks(""); }}
              disabled={!checkedIn || checkedOut || busy}
              data-testid="check-out-button"
              className="h-20 text-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <LogOutIcon className="w-6 h-6 mr-3" strokeWidth={2} />
              {checkedOut ? `Checked out at ${rec.check_out}` : "Check Out"}
            </Button>
            {rec?.remarks && (
              <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-[#FEF3C7]/40 border border-[#D97706]/20 text-sm">
                <MessageSquare className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" />
                <div className="text-[#92400E]"><span className="font-semibold">Today&apos;s remarks:</span> {rec.remarks}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 stagger">
          <SummaryCard label="Attendance %" value={`${summary.percentage}%`} accent="#0F2E4A" icon={CalendarDays} testid="sum-pct" />
          <SummaryCard label="Total Hours" value={`${summary.total_hours}h`} accent="#059669" icon={Timer} testid="sum-hours" />
          <SummaryCard label="Present" value={summary.present} accent="#059669" icon={Check} testid="sum-present" />
          <SummaryCard label="Late" value={summary.late} accent="#D97706" icon={Clock} testid="sum-late" />
          <SummaryCard label="Half Day" value={summary.half_day} accent="#0F2E4A" icon={LogOutIcon} testid="sum-half" />
          <SummaryCard label="Absent" value={summary.absent} accent="#DC2626" icon={X} testid="sum-absent" />
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E8F0]">
          <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Attendance History</h2>
          <p className="text-xs text-[#64748B] mt-0.5">All your check-in/out records, most recent first.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Check-in</th>
                <th className="px-4 py-3">Check-out</th>
                <th className="px-4 py-3 text-right">Hours</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Remarks</th>
              </tr>
            </thead>
            <tbody data-testid="attendance-list">
              {records.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#64748B]">No attendance recorded yet. Hit Check In to start.</td></tr>}
              {records.map((r) => (
                <tr key={r.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3 font-medium">{new Date(r.date).toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}</td>
                  <td className="px-4 py-3 text-[#475569]">
                    {r.check_in ? <span className="inline-flex items-center gap-1.5 font-mono-plex"><LogIn className="w-3.5 h-3.5 text-[#059669]" />{r.check_in}</span> : <span className="text-[#94A3B8]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#475569]">
                    {r.check_out ? <span className="inline-flex items-center gap-1.5 font-mono-plex"><LogOutIcon className="w-3.5 h-3.5 text-[#DC2626]" />{r.check_out}</span> : <span className="text-[#94A3B8]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-plex">{r.total_hours ? `${r.total_hours}h` : "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded" style={{ backgroundColor: `${COLORS[r.status]}15`, color: COLORS[r.status] }}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#475569] max-w-[240px] truncate">{r.remarks || <span className="text-[#94A3B8]">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Remarks dialog */}
      <Dialog open={!!actionKind} onOpenChange={(v) => { if (!v) { setActionKind(null); setRemarks(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionKind === "in" ? "Confirm Check-in" : "Confirm Check-out"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-[#64748B]">Current time (IST)</span>
                <span className="font-mono-plex font-semibold text-[#0F172A]">{istTime}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[#64748B]">Date</span>
                <span className="font-medium text-[#0F172A]">{today?.date}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#475569]">Optional Remarks</Label>
              <Textarea
                data-testid="remarks-input"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="e.g. Traffic delay, Doctor appointment, Working from offsite..."
                className="mt-1.5"
                rows={3}
              />
              <p className="text-[11px] text-[#64748B] mt-1.5">Tip: &quot;Late Arrival&quot; / &quot;Early Departure&quot; tags are added automatically when applicable.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={doAction}
              disabled={busy}
              data-testid="confirm-action-button"
              className={actionKind === "in" ? "bg-[#059669] hover:bg-[#047857] text-white" : "bg-[#DC2626] hover:bg-[#B91C1C] text-white"}
            >
              {busy ? "Saving..." : actionKind === "in" ? "Confirm Check-in" : "Confirm Check-out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, accent, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">{label}</div>
          <div className="mt-2 font-heading text-xl font-bold text-[#0F172A]">{value}</div>
        </div>
        <Icon className="w-4 h-4" strokeWidth={1.75} style={{ color: accent }} />
      </div>
    </div>
  );
}

/* ============================================================
 *  ADMIN / MANAGER  ATTENDANCE  (override / batch)
 * ============================================================ */

function AdminAttendance() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState("");
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});

  useEffect(() => {
    api.get("/batches")
      .then(({ data }) => {
        setBatches(data);
        if (data[0]) setBatch(data[0].name);
      })
      .catch((err) => toast.error(formatErr(err?.response?.data?.detail) || "Failed to load batches"));
  }, []);

  useEffect(() => {
    if (!batch) return;
    (async () => {
      try {
        const { data } = await api.get("/students", { params: { batch } });
        setStudents(data);
        const { data: att } = await api.get("/attendance", { params: { date_from: date, date_to: date } });
        const map = {};
        data.forEach((s) => {
          const found = att.find((a) => a.student_id === s.id);
          map[s.id] = {
            check_in: found?.check_in || "",
            check_out: found?.check_out || "",
            remarks: found?.remarks || "",
          };
        });
        setMarks(map);
      } catch (err) {
        toast.error(formatErr(err?.response?.data?.detail) || "Failed to load attendance data");
      }
    })();
  }, [batch, date]);

  const setField = (sid, field, value) => {
    setMarks({ ...marks, [sid]: { ...(marks[sid] || {}), [field]: value } });
  };

  const quickAllPresent = () => {
    const next = {};
    students.forEach((s) => { next[s.id] = { check_in: "09:00", check_out: "17:00", remarks: "" }; });
    setMarks(next);
  };

  const clearAll = () => {
    const next = {};
    students.forEach((s) => { next[s.id] = { check_in: "", check_out: "", remarks: "" }; });
    setMarks(next);
  };

  const save = async () => {
    const records = Object.entries(marks).map(([student_id, m]) => ({
      student_id,
      check_in: m.check_in || null,
      check_out: m.check_out || null,
      remarks: m.remarks || null,
    }));
    try {
      const { data } = await api.post("/attendance/bulk", { date, batch, records });
      toast.success(`Saved ${data.saved} records`);
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-admin">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Attendance</div>
        <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Override Attendance</h1>
        <p className="text-sm text-[#475569] mt-1">Students self check-in & check-out from their portal. Use this view to make corrections or mark for a past date.</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex flex-wrap gap-4 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#475569] font-semibold block mb-1.5">Date</label>
          <Input data-testid="attendance-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#475569] font-semibold block mb-1.5">Batch</label>
          <Select value={batch} onValueChange={setBatch}>
            <SelectTrigger className="w-56" data-testid="attendance-batch"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {batches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={quickAllPresent} data-testid="mark-all-present" className="text-[#059669] border-[#059669]/30 hover:bg-[#ECFDF5]">
            <Check className="w-4 h-4 mr-1.5" /> Mark All Present
          </Button>
          <Button variant="outline" onClick={clearAll} data-testid="clear-all" className="text-[#DC2626] border-[#DC2626]/30 hover:bg-[#FEF2F2]">
            <X className="w-4 h-4 mr-1.5" /> Clear All
          </Button>
          <Button onClick={save} className="bg-[#0F2E4A] hover:bg-[#174169] text-white" data-testid="save-attendance">
            <Check className="w-4 h-4 mr-1.5" /> Save
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
              <tr>
                <th className="px-3 py-3">Student ID</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3 w-32">Check-in</th>
                <th className="px-3 py-3 w-32">Check-out</th>
                <th className="px-3 py-3 w-20 text-right">Hours</th>
                <th className="px-3 py-3 w-24">Status</th>
                <th className="px-3 py-3">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {students.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-[#64748B]">Select a batch to begin.</td></tr>
              )}
              {students.map((s) => {
                const m = marks[s.id] || { check_in: "", check_out: "", remarks: "" };
                const status = deriveStatus(m.check_in, m.check_out);
                const hours = computeHours(m.check_in, m.check_out);
                return (
                  <tr key={s.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-3 py-2 font-mono-plex text-[#0F2E4A]">{s.student_id}</td>
                    <td className="px-3 py-2 font-medium text-[#0F172A]">{s.name}</td>
                    <td className="px-3 py-2">
                      <Input type="time" value={m.check_in} onChange={(e) => setField(s.id, "check_in", e.target.value)} data-testid={`check-in-${s.id}`} className="h-8" />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="time" value={m.check_out} onChange={(e) => setField(s.id, "check_out", e.target.value)} data-testid={`check-out-${s.id}`} className="h-8" />
                    </td>
                    <td className="px-3 py-2 text-right font-mono-plex text-[#0F172A]">{hours ? `${hours}h` : "—"}</td>
                    <td className="px-3 py-2">
                      <span data-testid={`status-${s.id}`} className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded" style={{ backgroundColor: `${COLORS[status]}15`, color: COLORS[status] }}>
                        {status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Input value={m.remarks} onChange={(e) => setField(s.id, "remarks", e.target.value)} placeholder="Optional" data-testid={`remarks-${s.id}`} className="h-8 text-xs" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function computeHours(ci, co) {
  if (!ci || !co) return 0;
  const [h1, m1] = ci.split(":").map(Number);
  const [h2, m2] = co.split(":").map(Number);
  const mins = h2 * 60 + m2 - (h1 * 60 + m1);
  return mins > 0 ? Math.round((mins / 60) * 100) / 100 : 0;
}
